import { lazyLoadView } from '@utils/LazyLoad'
import { FEATURES, SEARCH_GROUPS } from '@/src/constants/Text'
import store from '@state/store'
import { isWelcomeDismissed } from '@modules/design/utils/welcome-storage'
import {
  blockForCliente,
  isCliente,
  welcomeAndNotCliente,
} from '@modules/financial-planning/utils/client-permissions'
import { allowClienteWithFlag } from '@modules/financial-planning/utils/client-feature-gate'

// Ordem de precedência do redirect (matriz Cross-Sell × Flags FP):
// 0. cliente final → fp.home (antes de qualquer flag: as outras 4 saídas são
//    fp.welcome ou fp.simulacoes.pgbl, ambas bloqueadas pelo gate de perfil —
//    aterrissar nelas seria mandar o cliente direto para o 404)
// 1. FINANCIAL_PLANNING_SIMULATOR=TRUE + já dispensado (1ª visita concluída) → fp.home
// 2. FINANCIAL_PLANNING_SIMULATOR=TRUE + ainda não dispensado → fp.welcome (1ª visita,
//    mesma lógica de resolveCrmRedirect em @modules/crm/routes.js)
// 3. FINANCIAL_PLANNING=TRUE (sem simulator) → fp.simulacoes.pgbl (PGBL paga legada)
// 4. welcome acessível (OCULTAR_CROSS_SELL=FALSE/NULL) → fp.welcome (upsell)
// 5. fallback (OCULTAR_CROSS_SELL=TRUE + sem flags FP) → fp.simulacoes.pgbl (produto livre)
const resolveFinancialPlanningRedirect = () => {
  const getters = store.getters || {}
  const show = getters['parameters/show'] || {}
  const user = getters['user/currentUser'] || {}
  // ★ `user.loading` é o gate, e não `isCliente(user)` sozinho.
  //
  // `src/state/modules/user/state.js:28,34` nasce com `loading: true` E
  // `IS_CLIENTE: true`. Num COLD LOAD (bookmark, link, URL digitada) o
  // `redirect` é resolvido no `match()`, antes de `P.getCurrentUser()` — então
  // `isCliente()` responde `true` para TODO MUNDO, inclusive o profissional.
  //
  // Sem esta checagem, o profissional de escritório com PGBL legado ou com
  // cross-sell visível era mandado para `fp.home`, que `allowClienteWithFlag`
  // fecha para quem não tem `FINANCIAL_PLANNING_SIMULATOR` — 404 no ponto de
  // entrada do módulo. Não aparecia em teste manual porque a sidebar aponta
  // direto para `fp.home` (warm), nunca para a raiz.
  //
  // Com a identidade indisponível, a decisão cabe às flags (a matriz de
  // sempre); quem corrige o destino do CLIENTE é `applyClienteFpRedirectGuard`
  // (`router/index.js`), que roda depois do `await P.getCurrentUser()`.
  if (!user.loading && isCliente(user)) {
    return { name: 'fp.home' }
  }
  if (show[FEATURES.FINANCIAL_PLANNING_SIMULATOR]) {
    if (isWelcomeDismissed('financial-planning')) {
      return { name: 'fp.home' }
    }
    return { name: 'fp.welcome' }
  }
  if (show[FEATURES.FINANCIAL_PLANNING]) {
    return { name: 'fp.simulacoes.pgbl' }
  }
  if (!show[FEATURES.OCULTAR_CROSS_SELL]) {
    return { name: 'fp.welcome' }
  }
  return { name: 'fp.simulacoes.pgbl' }
}

export default [
  {
    path: '/financial-planning',
    name: 'fp',
    // Raiz sempre acessível: o redirect (resolveFinancialPlanningRedirect) escolhe o destino
    // correto conforme as flags do escritório. O gate de upsell vive isolado em fp.welcome.
    // A Calculadora PGBL é produto livre — não pode ser bloqueada via gate na raiz.
    meta: {
      search: {
        title: 'Financial Planning',
        description: 'Planejamento financeiro, simulações e objetivos',
        keywords: [
          'planejamento',
          'financial planning',
          'simulações',
          'objetivos',
          'pgbl',
        ],
        icon: 'mdi-chart-timeline-variant',
        group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
      },
    },
    redirect: resolveFinancialPlanningRedirect,
    component: () =>
      lazyLoadView(
        import(
          '@modules/financial-planning/views/nav-bar/financial-planning-nav-bar.vue'
        )
      ),
    children: [
      {
        path: 'bem-vindo',
        name: 'fp.welcome',
        // customPermission: welcome é acessível como upsell quando OCULTAR_CROSS_SELL está ausente;
        // retorna 404 apenas quando OCULTAR_CROSS_SELL=true e o escritório não tem nenhuma das flags FP.
        // welcomeAndNotCliente COMPÕE com essa matriz (não a substitui): o cliente
        // final é reprovado sempre, os demais perfis mantêm o comportamento atual.
        meta: {
          customPermission: welcomeAndNotCliente,
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/financial-planning/views/welcome/view-fp-welcome.vue'
            )
          ),
      },
      {
        path: 'home',
        name: 'fp.home',
        meta: {
          // SEM can_show — e isso é o gate, não a ausência dele (RF-B39).
          // O guard aplica can_show a TODO perfil, ANTES do customPermission
          // (router/index.js:207-237), então um can_show aqui condicionaria o
          // cliente ao FINANCIAL_PLANNING_SIMULATOR e o mandaria ao 404 num
          // escritório que ligou só a flag dele. allowClienteWithFlag decide os
          // dois ramos: cliente → FINANCIAL_PLANNING_CLIENTE; demais perfis →
          // FINANCIAL_PLANNING_SIMULATOR, idêntico ao que o can_show fazia.
          customPermission: allowClienteWithFlag,
          // Porta declarativa de viewport lida por app.vue (routeAllowsMobile).
          // Só as 3 telas do cliente têm layout mobile; abrir a porta numa rota
          // bloqueada seria vazamento silencioso — o gate de perfil devolveria
          // 404, mas o app inteiro destravaria no celular.
          allowMobile: true,
          search: {
            title: 'Visão Geral do Planejamento',
            description: 'Dashboard do planejamento financeiro do cliente',
            keywords: ['visão geral', 'dashboard', 'planejamento', 'resumo'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/financial-planning/views/view-home.vue')
          ),
      },
      {
        path: 'transacoes',
        name: 'fp.transacoes',
        meta: {
          // Sem can_show, pelo mesmo motivo de fp.home (RF-B39).
          customPermission: allowClienteWithFlag,
          allowMobile: true,
          search: {
            title: 'Transações',
            description: 'Orçamentos e transações do planejamento financeiro',
            keywords: [
              'transações',
              'orçamentos',
              'receitas',
              'despesas',
              'gastos',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/financial-planning/views/view-orcamentos.vue')
          ),
      },
      {
        path: 'objetivos',
        name: 'fp.objetivos',
        meta: {
          // Sem can_show, pelo mesmo motivo de fp.home (RF-B39).
          customPermission: allowClienteWithFlag,
          allowMobile: true,
          search: {
            title: 'Objetivos',
            description: 'Metas e objetivos financeiros do cliente',
            keywords: ['objetivos', 'metas', 'sonhos', 'planejamento'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/financial-planning/views/view-objetivos.vue')
          ),
      },
      {
        path: 'simulacoes',
        name: 'fp.simulacoes',
        meta: {
          can_show: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
          // Gate de perfil ao LADO do can_show — são independentes e ambos
          // precisam passar. Cada rota declara o seu: vue-router não mescla
          // meta entre pai e filho (ver comentário em fp.simulacoes.planejamento).
          customPermission: blockForCliente,
          search: {
            title: 'Simulações',
            description: 'Simulações de planejamento financeiro e cenários',
            keywords: ['simulações', 'cenários', 'projeções', 'planejamento'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/financial-planning/views/view-simulacoes-wrapper.vue'
            )
          ),
        redirect: { name: 'fp.simulacoes.planejamento' },
        children: [
          {
            path: 'planejamento',
            name: 'fp.simulacoes.planejamento',
            // meta próprio é OBRIGATÓRIO aqui: vue-router NÃO mescla meta entre
            // pai e filho (route.meta = record.meta || {}, sem herança — ver
            // node_modules/vue-router/dist/vue-router.common.js:161). Contar
            // com o can_show do pai (fp.simulacoes) pra "herdar" o gate era
            // uma suposição errada — o guard (router/index.js:187) nunca via
            // esse can_show e liberava a aba sem checar FINANCIAL_PLANNING_SIMULATOR.
            meta: {
              can_show: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
              customPermission: blockForCliente,
            },
            component: () =>
              lazyLoadView(
                import('@modules/financial-planning/views/view-simulacoes.vue')
              ),
          },
          {
            path: 'cenario-fiscal',
            name: 'fp.simulacoes.pgbl',
            // Calculadora PGBL é produto livre — sem can_show; sempre acessível
            // (alinha com decisão do PR #1004 mergeada em desenvolvimento).
            // "Livre" vale para os ESCRITÓRIOS, não para o cliente final: a
            // calculadora é ferramenta do profissional (todo resource PGBL do
            // backend exige decorator_check_is_professional e devolve 403 ao
            // cliente). Por isso as 4 rotas da subárvore declaram gate de
            // perfil próprio — e apenas ele, sem can_show.
            meta: {
              customPermission: blockForCliente,
              search: {
                title: 'Calculadora PGBL',
                description: 'Cálculo de cenário fiscal e vantagem do PGBL',
                keywords: [
                  'pgbl',
                  'calculadora',
                  'imposto de renda',
                  'previdência',
                  'fiscal',
                ],
                group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
              },
            },
            component: () =>
              lazyLoadView(
                import(
                  '@modules/financial-planning/views/calculadora-pgbl/view-calculadora-pgbl.vue'
                )
              ),
            redirect: { name: 'fp.simulacoes.pgbl.simulacoes' },
            children: [
              {
                path: 'lista',
                name: 'fp.simulacoes.pgbl.simulacoes',
                meta: {
                  customPermission: blockForCliente,
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/financial-planning/views/table-simulations/view-table-simulations.vue'
                    )
                  ),
              },
              {
                path: 'edicao-dados',
                name: 'fp.simulacoes.pgbl.edicao-dados',
                meta: {
                  customPermission: blockForCliente,
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/financial-planning/views/table-calculadora-pgbl/view-table-calculadora-pgbl.vue'
                    )
                  ),
              },
              {
                path: 'resultados',
                name: 'fp.simulacoes.pgbl.resultado',
                meta: {
                  customPermission: blockForCliente,
                },
                component: () =>
                  lazyLoadView(
                    import(
                      '@modules/financial-planning/views/result-simulation-pgbl/view-result-simulation-pgbl.vue'
                    )
                  ),
              },
            ],
          },
        ],
      },
      {
        path: 'laboratorio',
        name: 'fp.laboratorio',
        meta: {
          can_show: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
          customPermission: blockForCliente,
          search: {
            title: 'Laboratório de Simulações',
            description: 'Ambiente de testes para simulações financeiras',
            keywords: ['laboratório', 'simulações', 'testes', 'experimentos'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import(
              '@modules/financial-planning/views/view-laboratorio-simulacoes.vue'
            )
          ),
      },
      {
        path: 'contas',
        name: 'fp.contas',
        meta: {
          can_show: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
          customPermission: blockForCliente,
          search: {
            title: 'Contas de Investimento',
            description: 'Configuração de contas e conexões Open Finance',
            keywords: [
              'contas',
              'open finance',
              'configurações',
              'conexões',
              'bancos',
            ],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/financial-planning/views/view-configuracoes.vue')
          ),
      },
      {
        path: 'relatorios',
        name: 'fp.relatorios',
        meta: {
          can_show: FEATURES.FINANCIAL_PLANNING_SIMULATOR,
          customPermission: blockForCliente,
          search: {
            title: 'Relatórios do Planejamento',
            description: 'Relatórios do planejamento financeiro do cliente',
            keywords: ['relatórios', 'planejamento', 'exportar', 'documentos'],
            group: SEARCH_GROUPS.GESTAO_OPERACIONAL,
          },
        },
        component: () =>
          lazyLoadView(
            import('@modules/financial-planning/views/view-relatorios.vue')
          ),
      },

      // Backward compatibility redirects for old routes
      {
        path: 'pgbl',
        redirect: { name: 'fp.simulacoes.pgbl' },
      },
      {
        path: 'pgbl/simulacoes',
        redirect: { name: 'fp.simulacoes.pgbl.simulacoes' },
      },
      {
        path: 'pgbl/edicao-dados',
        redirect: { name: 'fp.simulacoes.pgbl.edicao-dados' },
      },
      {
        path: 'pgbl/resultados',
        redirect: { name: 'fp.simulacoes.pgbl.resultado' },
      },
      {
        path: 'receita-despesas',
        redirect: { name: 'fp.simulacoes.planejamento' },
      },
      {
        path: 'goals',
        redirect: { name: 'fp.objetivos' },
      },
      {
        path: 'assets',
        redirect: { name: 'fp.objetivos' },
      },
    ],
  },
]
